import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'witchy_box',
    price: '',
    images: '',
    is_digital: false,
    digital_file: '',
    stock: '',
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'witchy_box',
        price: product.price || '',
        images: product.images ? product.images.join('\n') : '',
        is_digital: product.is_digital || false,
        digital_file: product.digital_file || '',
        stock: product.stock || '',
        active: product.active !== undefined ? product.active : true
      });
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        description: '',
        category: 'witchy_box',
        price: '',
        images: '',
        is_digital: false,
        digital_file: '',
        stock: '',
        active: true
      });
    }
  }, [product, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        price: parseInt(formData.price, 10),
        images: formData.images.split('\n').filter(img => img.trim()),
        is_digital: formData.is_digital,
        digital_file: formData.digital_file || null,
        stock: parseInt(formData.stock, 10),
        active: formData.active
      };

      if (product) {
        // Update existing product
        await axios.put(`${API_URL}/products/${product.id}`, productData);
      } else {
        // Create new product
        await axios.post(`${API_URL}/products`, productData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt a mentés során');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('A kép maximum 5MB lehet!');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Csak JPEG, PNG, WEBP és GIF formátumok engedélyezettek!');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/products/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Add the uploaded image URL to the images list
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({
        ...prev,
        images: prev.images ? `${prev.images}\n${imageUrl}` : imageUrl
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Hiba történt a kép feltöltése során');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    const imageList = formData.images.split('\n').filter(img => img.trim());
    const updatedImages = imageList.filter((_, index) => index !== indexToRemove);
    setFormData(prev => ({
      ...prev,
      images: updatedImages.join('\n')
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="glass rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black/40 backdrop-blur-sm p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {product ? 'Termék szerkesztése' : 'Új termék'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Termék neve <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="pl. Premium Witchy Box"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Leírás <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
              placeholder="Részletes termékleírás..."
            />
          </div>

          {/* Category and Price */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Kategória <span className="text-red-400">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              >
                <option value="witchy_box">Witchy Box</option>
                <option value="spell_jar">Spell Jar</option>
                <option value="crystal">Ásványok</option>
                <option value="digital">Digitális termékek</option>
                <option value="incense">Füstölők</option>
                <option value="cards">Kártyák</option>
                <option value="books">Könyvek</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Ár (HUF) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="1"
                className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                placeholder="4990"
              />
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Képek
            </label>
            
            {/* Upload Button */}
            <div className="mb-3">
              <label className="btn-magic rounded-lg px-4 py-2 inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImage}
                />
                {uploadingImage ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Feltöltés...
                  </>
                ) : (
                  <>
                    📸 Kép feltöltése
                  </>
                )}
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Max 5MB • JPEG, PNG, WEBP, GIF
              </p>
            </div>

            {/* Image URLs Textarea */}
            <textarea
              name="images"
              value={formData.images}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none font-mono text-sm"
              placeholder="Vagy írj be URL-t soronként:&#10;https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
            />
            <p className="text-xs text-gray-500 mt-1">
              A feltöltött képek automatikusan hozzáadódnak. Vagy írj be külső URL-eket.
            </p>

            {/* Image Preview */}
            {formData.images && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.images.split('\n').filter(img => img.trim()).map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-white/10 group">
                    <img
                      src={img}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23333" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3E❌%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {/* Delete button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      title="Kép törlése"
                    >
                      <span className="text-white text-2xl">🗑️</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stock */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Készlet (db) <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              required
              min="0"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="10"
            />
          </div>

          {/* Digital Product */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_digital"
                checked={formData.is_digital}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="text-gray-300">Digitális termék (pl. PDF, videó)</span>
            </label>

            {formData.is_digital && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Digitális fájl URL vagy neve
                </label>
                <input
                  type="text"
                  name="digital_file"
                  value={formData.digital_file}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                  placeholder="tarot-guide.pdf"
                />
              </div>
            )}
          </div>

          {/* Active */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span className="text-gray-300">Aktív (látható a weboldalon)</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 glass rounded-lg py-3 hover:bg-white/5 transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-magic rounded-lg py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Mentés...' : (product ? 'Módosítás' : 'Létrehozás')}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: '';
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default ProductModal;